import { Box, Flex, Heading, Text } from '@ghq-abi/design-system';
import { useTranslate } from '@tolgee/react';
import { CoiSpecificData } from '~/entities/MyTickets/types';
import { getFieldTranslationKey } from '~/entities/MyTickets/utils';
import { ListCardDetailsItem } from '../ListCard/ListCardDetailsItem';

interface CoiDetailsProps {
  employeeId: string;
  band: string;
  positionTitle: string;
  description: string;
  specificData: CoiSpecificData;
}

export function CoiDetails({
  employeeId,
  band,
  positionTitle,
  description,
  specificData,
}: CoiDetailsProps) {
  const { t } = useTranslate();

  const getFieldDisplayCount = () => {
    const baseFields = 3;
    const dynamicFieldsCount = specificData.extractedFields 
      ? Object.values(specificData.extractedFields).filter(value => value && value.trim() !== '').length 
      : 2;
    return baseFields + dynamicFieldsCount;
  };

  const getGridColumns = () => {
    const fieldCount = getFieldDisplayCount();
    if (fieldCount <= 4) return 'repeat(4, 1fr)';
    if (fieldCount <= 6) return 'repeat(3, 1fr)';
    return 'repeat(2, 1fr)';
  };

  const getDescriptionValue = () => {
    if (specificData.extractedFields) {
      const descriptionField = Object.entries(specificData.extractedFields)
        .find(([label]) => label.toLowerCase().includes('description'));
      if (descriptionField) {
        return descriptionField[1];
      }
    }
    return description;
  };

  const translateValueIfNeeded = (value: string): string => {
    if (value.startsWith('form.coi.')) return t(value);
    if (value === 'true') return t('common.yes');
    if (value === 'false') return t('common.no');
    
    return value;
  };


  const getTranslatedLabel = (label: string, questionKey?: string) => {
    if (questionKey) {
      const translationKey = getFieldTranslationKey(questionKey);
      return t(translationKey);
    }
    return label;
  };

  const renderDynamicFields = () => {
    if (!specificData.extractedFields) {
      return (
        <>
          <ListCardDetailsItem
            label={getTranslatedLabel('Other Party Involved', 'form.coi.relationship.employees.otherPartyName.question')}
            value={translateValueIfNeeded(specificData.otherPartyInvolved)}
          />
          <ListCardDetailsItem
            label={getTranslatedLabel('Relationship Type', 'form.coi.relationship.type.question')}
            value={translateValueIfNeeded(specificData.relationshipType)}
          />
        </>
      );
    }

    const fieldsToRender: JSX.Element[] = [];
    const detectedFields = specificData.detectedFields || [];
    
    Object.entries(specificData.extractedFields).forEach(([label, value], index) => {
      if (value && value.trim() !== '' && !label.toLowerCase().includes('description')) {
        const questionKey = detectedFields[index + 3];
        const translatedLabel = getTranslatedLabel(label, questionKey);
        const translatedValue = translateValueIfNeeded(value);
        
        fieldsToRender.push(
          <ListCardDetailsItem
            key={label}
            label={translatedLabel}
            value={translatedValue}
          />
        );
      }
    });

    return fieldsToRender;
  };

  const dynamicFields = renderDynamicFields();
  const hasMultipleFields = Array.isArray(dynamicFields) ? dynamicFields.length > 4 : false;

  return (
    <Box
      css={{
        marginTop: '$4',
        padding: '$2',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        border: '1px solid #EEEFF2',
        mx: '$24',
      }}
    >
      <Flex justify="between" css={{ flexWrap: 'wrap' }}>
        <Flex direction="column" css={{ 
          m: '$4', 
          width: hasMultipleFields ? '100%' : '50%',
          minWidth: hasMultipleFields ? '100%' : '400px'
        }}>
          <Heading css={{ fontSize: '$sm', mb: '$4' }}>
            {t('my_tickets.list.details.employee_details')}
          </Heading>
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: getGridColumns(),
              gap: '$4',
              '@md': {
                gridTemplateColumns: hasMultipleFields ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)'
              },
              '@sm': {
                gridTemplateColumns: 'repeat(2, 1fr)'
              }
            }}
          >
            <ListCardDetailsItem
              label={t('my_tickets.list.details.employee_id')}
              value={employeeId}
            />
            <ListCardDetailsItem
              label={t('my_tickets.list.details.employee_band')}
              value={band}
            />
            <ListCardDetailsItem
              label={t('my_tickets.list.details.position_title')}
              value={positionTitle}
            />
            {dynamicFields}
          </Box>
        </Flex>
        
        {!hasMultipleFields && (
          <Flex direction="column" css={{ m: '$4', width: '45%', minWidth: '300px' }}>
            <Heading css={{ fontSize: '$sm', mb: '$4' }}>
              {t('common.description')}
            </Heading>
            <Text css={{ 
              lineHeight: '$relaxed',
              fontSize: '$sm',
              wordBreak: 'break-word'
            }}>
              {getDescriptionValue()}
            </Text>
          </Flex>
        )}
      </Flex>
      
      {hasMultipleFields && (
        <Box css={{ m: '$4', mt: '$6' }}>
          <Heading css={{ fontSize: '$sm', mb: '$3' }}>
            {t('common.description')}
          </Heading>
          <Text css={{ 
            lineHeight: '$relaxed',
            fontSize: '$sm',
            wordBreak: 'break-word'
          }}>
            {getDescriptionValue()}
          </Text>
        </Box>
      )}
    </Box>
  );
}
